import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Grid,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PhotoCamera as CameraIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { processReceipt, getReceiptHistory, ReceiptItem, ReceiptProcessingResult } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ReceiptProcessing: React.FC = () => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ReceiptProcessingResult | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      setResult(null);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleProcessReceipt = async () => {
    if (!selectedFile) return;

    setProcessing(true);
    setError('');
    setResult(null);

    try {
      const response = await processReceipt(selectedFile);
      setResult(response.data.data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to process receipt';
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleImportToItems = () => {
    if (result) {
      setShowImportDialog(true);
    }
  };

  const handleDownloadCSV = () => {
    if (result) {
      const blob = new Blob([result.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'receipt-items.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleCloseImportDialog = () => {
    setShowImportDialog(false);
  };

  const handleConfirmImport = () => {
    // This will be implemented to use the existing CSV import functionality
    // For now, we'll just close the dialog
    setShowImportDialog(false);
    // TODO: Integrate with existing CSV import
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        AI Receipt Processing
      </Typography>
      
      <Typography variant="body1" color="text.secondary" mb={3}>
        Upload a photo of a store receipt and let AI extract the items automatically.
      </Typography>

      {/* File Upload Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Receipt
          </Typography>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />

          <Box display="flex" gap={2} mb={2}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleUploadClick}
              disabled={processing}
            >
              Select Image
            </Button>
            
            {selectedFile && (
              <Button
                variant="contained"
                startIcon={<CameraIcon />}
                onClick={handleProcessReceipt}
                disabled={processing}
              >
                Process with AI
              </Button>
            )}
          </Box>

          {selectedFile && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedFile.name}
              </Typography>
              {previewUrl && (
                <Box mt={2}>
                  <img 
                    src={previewUrl} 
                    alt="Receipt preview" 
                    style={{ maxWidth: '300px', maxHeight: '200px', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>
          )}

          {processing && (
            <Box mt={2}>
              <Typography variant="body2" mb={1}>
                Processing receipt with AI...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Results Section */}
      {result && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Processing Results
              </Typography>
              <Box display="flex" gap={1}>
                <Chip 
                  icon={<CheckIcon />}
                  label={`${result.itemCount} items found`}
                  color="success"
                  size="small"
                />
              </Box>
            </Box>

            <Box display="flex" gap={2} mb={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCSV}
                sx={{ flex: 1 }}
              >
                Download CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={handleImportToItems}
                sx={{ flex: 1 }}
              >
                Import to Items
              </Button>
            </Box>

            {/* Items Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item Name</TableCell>
                    <TableCell align="right">Cost (€)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">€{item.cost.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Import Confirmation Dialog */}
      <Dialog open={showImportDialog} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Items to Database</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will import {result?.itemCount} items from the receipt into your items database.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Items will be added with the following settings:
          </Typography>
          <Box mt={1}>
            <Typography variant="body2">
              • <strong>Code:</strong> Auto-generated based on item name
            </Typography>
            <Typography variant="body2">
              • <strong>Cost:</strong> As shown on receipt
            </Typography>
            <Typography variant="body2">
              • <strong>Cost:</strong> Estimated based on typical markup
            </Typography>
            <Typography variant="body2">
              • <strong>Inventory:</strong> +1 to "On Hand" quantity
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>Cancel</Button>
          <Button onClick={handleConfirmImport} variant="contained">
            Import Items
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiptProcessing;
